import { A, useSearchParams } from "@solidjs/router";
import { createMemo, createResource, createSignal, onMount, Show } from "solid-js";

const STORAGE_KEY = "fs-stripe-cs-id";

type LookupResult = {
  email: string;
  display_token: string;
  source: string;
};

async function lookup(csId: string): Promise<LookupResult | null> {
  if (!csId) return null;
  const res = await fetch(`/api/checkout/lookup?cs_id=${encodeURIComponent(csId)}`, {
    credentials: "same-origin",
  });
  if (!res.ok) return null;
  return (await res.json()) as LookupResult;
}

export default function ThankYou() {
  const [params] = useSearchParams();
  const [storedRef, setStoredRef] = createSignal<string | null>(null);
  const [copied, setCopied] = createSignal<"none" | "ref" | "token">("none");

  const cs = createMemo<string | null>(() => {
    const fromUrl = typeof params.cs_id === "string" ? params.cs_id : null;
    return fromUrl ?? storedRef();
  });

  const [details] = createResource(cs, async (id) => (id ? await lookup(id) : null));

  onMount(() => {
    const fromUrl = typeof params.cs_id === "string" ? params.cs_id : null;
    try {
      if (fromUrl) {
        localStorage.setItem(STORAGE_KEY, fromUrl);
      } else {
        const cached = localStorage.getItem(STORAGE_KEY);
        if (cached) setStoredRef(cached);
      }
    } catch {
      /* localStorage unavailable: degrade silently */
    }
  });

  const copyTo = async (key: "ref" | "token", text: string | null) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied((k) => (k === key ? "none" : k)), 1500);
    } catch {
      /* ignore */
    }
  };

  return (
    <section class="thankyou-page">
      <div class="thankyou-card">
        <div class="thankyou-eyebrow">PAYMENT RECEIVED</div>
        <h2 class="thankyou-title">Thanks for buying Fortune 500 Business Models.</h2>
        <p class="thankyou-lead">
          We just emailed a sign-in link to{" "}
          <Show when={details()?.email} fallback={<strong>the email you used at checkout</strong>}>
            <strong>{details()!.email}</strong>
          </Show>
          . Click it and you're in. Link expires in 15 minutes; you can request another any time.
        </p>

        <div class="thankyou-actions">
          <A class="thankyou-btn primary" href="/login">
            Go to sign in →
          </A>
          <A class="thankyou-btn" href="/">
            Back to home
          </A>
        </div>

        <Show when={details()}>
          <div class="thankyou-ref">
            <div class="thankyou-ref-row">
              <span class="thankyou-ref-k">Access reference</span>
              <button
                type="button"
                class="thankyou-ref-copy"
                onClick={() => copyTo("token", details()!.display_token)}
              >
                {copied() === "token" ? "copied" : "copy"}
              </button>
            </div>
            <code class="thankyou-ref-v">{details()!.display_token}</code>
            <p class="thankyou-ref-hint">
              Lost the email? Paste this reference at{" "}
              <a class="thankyou-link" href="/login">
                /login
              </a>{" "}
              to get a fresh sign-in link. Or email{" "}
              <a class="thankyou-link" href="mailto:vitalii@connectingdots.fyi">
                vitalii@connectingdots.fyi
              </a>{" "}
              with this reference and we'll resend by hand.
            </p>
          </div>
        </Show>

        <Show when={cs() && !details.loading && !details()}>
          <div class="thankyou-ref">
            <div class="thankyou-ref-row">
              <span class="thankyou-ref-k">Stripe reference</span>
              <button
                type="button"
                class="thankyou-ref-copy"
                onClick={() => copyTo("ref", cs())}
              >
                {copied() === "ref" ? "copied" : "copy"}
              </button>
            </div>
            <code class="thankyou-ref-v">{cs()}</code>
            <p class="thankyou-ref-hint">
              Stripe confirmed your payment but we couldn't load your access reference yet (the
              webhook may still be processing). Email{" "}
              <a class="thankyou-link" href="mailto:vitalii@connectingdots.fyi">
                vitalii@connectingdots.fyi
              </a>{" "}
              with this reference if you don't get the sign-in email within a few minutes.
            </p>
          </div>
        </Show>
      </div>
    </section>
  );
}
